import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Receipt, CalendarDays, HardHat, CheckCircle2, XCircle, ExternalLink, Pencil, TrendingUp, Clock, Package, User, BookOpen, Plus } from 'lucide-react';
import { formatPhoneNumber } from './PhoneInput';

const money = (c) => `$${((c || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
const cardCls = 'rounded-xl bg-[#161616] border border-gray-800/80 overflow-hidden max-w-sm w-full';
const Head = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 px-4 pt-3">
    <Icon size={14} className="text-[#D4AF37]" />
    <span className="font-brand text-[10px] font-bold uppercase tracking-[0.18em] text-[#D4AF37]">{label}</span>
  </div>
);
const Row = ({ k, v, gold }) => (
  <div className="flex justify-between gap-3 text-xs py-0.5">
    <span className="text-gray-500">{k}</span>
    <span className={`${gold ? 'text-[#D4AF37] font-semibold' : 'text-gray-200'} text-right`}>{v ?? '—'}</span>
  </div>
);
const Btn = ({ onClick, children, primary, ...rest }) => (
  <button onClick={onClick} {...rest} className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold rounded-lg py-2 transition-colors disabled:opacity-50 ${primary ? 'bg-[#D4AF37] text-black hover:bg-[#B8941F]' : 'border border-gray-700 text-gray-300 hover:bg-white/5'}`}>{children}</button>
);

const QuoteCard = ({ d }) => {
  const navigate = useNavigate();
  return (
    <div className={cardCls} data-testid="foreman-quote-card">
      <Head icon={FileText} label="Quote" />
      <div className="px-4 py-2.5">
        <div className="text-white font-semibold text-sm">{d.quote_number} · {d.title}</div>
        <div className="mt-1.5">
          <Row k="Customer" v={d.customer_name} />
          <Row k="Status" v={<span className={d.status === 'accepted' ? 'text-green-400' : d.status === 'draft' ? 'text-gray-300' : 'text-amber-400'}>{(d.status || '').toUpperCase()}</span>} />
          <Row k="Lines" v={d.line_count} />
          {d.discount_cents > 0 && <Row k={`Discount (${d.discount_percent}%)`} v={`−${money(d.discount_cents)}`} />}
          <Row k="Total" v={d.total_cents != null ? money(d.total_cents) : 'Incomplete pricing'} gold />
          {d.valid_until && <Row k="Valid until" v={d.valid_until} />}
        </div>
        {d.lines?.length > 0 && (
          <div className="mt-2 border-t border-gray-800 pt-2 space-y-0.5 max-h-36 overflow-y-auto">
            {d.lines.map((l) => (
              <div key={l.id} className="flex justify-between gap-2 text-[11px]">
                <span className="text-gray-400 truncate">{l.name}{l.origin_type === 'custom' && <span className="text-[#D4AF37]/80"> · Custom</span>}</span>
                <span className="text-gray-300 shrink-0">{l.quantity_decimal} × {money(l.customer_unit_price_cents)} = {money(l.customer_line_total_cents)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="flex gap-2 px-4 pb-3">
        <Btn primary onClick={() => navigate(`/ops/quotes/${d.id}`)}><ExternalLink size={12} /> Open Quote</Btn>
        {d.status === 'draft' && <Btn onClick={() => navigate(`/ops/quotes/${d.id}`)}><Pencil size={12} /> Edit Draft</Btn>}
      </div>
    </div>
  );
};

const ExpenseCard = ({ d }) => (
  <div className={cardCls} data-testid="foreman-expense-card">
    <Head icon={Receipt} label="Expense" />
    <div className="px-4 py-2.5 pb-3.5">
      <Row k="Vendor" v={d.vendor_name} />
      {d.description && <Row k="Description" v={d.description} />}
      {d.project_name && <Row k="Project" v={d.project_name} />}
      <Row k="Category" v={d.work_category || d.expense_type} />
      <Row k="Amount" v={money(d.total_cents)} gold />
      <Row k="Date" v={d.expense_date} />
    </div>
  </div>
);

const EventCard = ({ d }) => (
  <div className={cardCls} data-testid="foreman-event-card">
    <Head icon={CalendarDays} label="Calendar Event" />
    <div className="px-4 py-2.5 pb-3.5">
      <div className="text-white font-semibold text-sm mb-1">{d.title}</div>
      <Row k="Date" v={`${d.start_date}${d.start_time ? ` · ${d.start_time}${d.end_time ? `–${d.end_time}` : ''}` : ' · All day'}`} />
      {d.project_name && <Row k="Project" v={d.project_name} />}
      {d.location && <Row k="Location" v={d.location} />}
      <Row k="Type" v={(d.event_type || '').replace('_', ' ')} />
    </div>
  </div>
);

const ProjectCard = ({ d }) => {
  const navigate = useNavigate();
  return (
    <div className={cardCls} data-testid="foreman-project-card">
      <Head icon={HardHat} label="Project" />
      <div className="px-4 py-2.5">
        <div className="text-white font-semibold text-sm">{d.name}</div>
        <div className="mt-1.5">
          <Row k="Customer" v={d.customer_name} />
          <Row k="Status" v={(d.status || '').replace('_', ' ')} />
          <Row k="Contract" v={money(d.contract_value_cents)} gold />
          {d.quote_number && <Row k="From quote" v={d.quote_number} />}
        </div>
      </div>
      <div className="flex gap-2 px-4 pb-3">
        <Btn primary onClick={() => navigate(`/ops/projects/${d.id}`)}><ExternalLink size={12} /> Open Project</Btn>
      </div>
    </div>
  );
};

const HEALTH = {
  under_budget: ['text-green-400', 'Under Budget'],
  at_budget: ['text-amber-400', 'At Budget'],
  over_budget: ['text-red-400', 'Over Budget'],
  no_budget: ['text-gray-500', 'No Budget Set'],
};

const FinancialCard = ({ d }) => {
  const navigate = useNavigate();
  const [hc, hl] = HEALTH[d.budget_health] || HEALTH.no_budget;
  const margin = d.margin_bps != null ? `${(d.margin_bps / 100).toFixed(1)}%` : '—';
  return (
    <div className={cardCls} data-testid="foreman-financial-card">
      <Head icon={TrendingUp} label="Financial Summary" />
      <div className="px-4 py-2.5">
        <div className="text-white font-semibold text-sm">{d.project_name}</div>
        <div className="mt-1.5">
          <Row k="Revenue" v={d.revenue_cents != null ? money(d.revenue_cents) : 'No budget set'} />
          {d.change_order_count > 0 && <Row k={`Change orders (${d.change_order_count})`} v={money(d.change_order_cents)} />}
          <Row k="Materials" v={money(d.materials_cents)} />
          <Row k="Labor" v={money(d.labor_cents)} />
          <Row k="Subcontractors" v={money(d.subs_cents)} />
          <Row k="Total Cost" v={money(d.total_costs_cents)} />
          <Row k="Potential GP" v={d.gp_cents != null ? money(d.gp_cents) : '—'} gold />
          <Row k="Margin" v={margin} gold />
          <Row k="Budget Health" v={<span className={hc}>{hl}</span>} />
        </div>
        {d.warnings?.length > 0 && <p className="text-[10px] text-amber-500/80 mt-2">{d.warnings.join(' ')}</p>}
      </div>
      <div className="flex gap-2 px-4 pb-3">
        <Btn primary onClick={() => navigate(`/ops/projects/${d.project_id}`)}><ExternalLink size={12} /> Open Project</Btn>
        <Btn onClick={() => navigate('/ops/financials')}>Financials</Btn>
      </div>
    </div>
  );
};

const LaborCard = ({ d }) => (
  <div className={cardCls} data-testid="foreman-labor-card">
    <Head icon={Clock} label="Labor Summary" />
    <div className="px-4 py-2.5 pb-3.5">
      <div className="text-gray-500 text-[11px] mb-1.5">{d.scope}</div>
      {(d.rows || []).map((r) => <Row key={r.label} k={r.label} v={`${r.hours} hrs · ${money(r.cost_cents)}`} />)}
      <div className="border-t border-gray-800 mt-1.5 pt-1.5">
        <Row k="Total" v={`${d.total_hours} hrs · ${money(d.total_cost_cents)}`} gold />
      </div>
    </div>
  </div>
);

const MaterialCard = ({ d }) => (
  <div className={cardCls} data-testid="foreman-material-card">
    <Head icon={Package} label="Material & Vendor Spend" />
    <div className="px-4 py-2.5 pb-3.5">
      <div className="text-gray-500 text-[11px] mb-1.5">{d.scope}</div>
      {(d.rows || []).map((r) => <Row key={r.label} k={`${r.label} (${r.count})`} v={money(r.cost_cents)} />)}
      <div className="border-t border-gray-800 mt-1.5 pt-1.5">
        <Row k="Total" v={money(d.total_cents)} gold />
      </div>
    </div>
  </div>
);

const CustomerCard = ({ d }) => {
  const navigate = useNavigate();
  return (
    <div className={cardCls} data-testid="foreman-customer-card">
      <Head icon={User} label="Customer" />
      <div className="px-4 py-2.5 pb-3.5">
        <div className="text-white font-semibold text-sm">{d.name}</div>
        <div className="mt-1.5">
          {d.address && <Row k="Address" v={d.address} />}
          {d.phone && <Row k="Phone" v={formatPhoneNumber(d.phone)} />}
          {d.email && <Row k="Email" v={d.email} />}
          <Row k="Quotes" v={d.quotes?.length || 0} />
        </div>
        {d.active_projects?.length > 0 && (
          <div className="mt-2 border-t border-gray-800 pt-2">
            <span className="text-[10px] text-[#D4AF37] font-brand font-bold uppercase tracking-wider">Active Projects</span>
            {d.active_projects.map((p) => (
              <button key={p.id} onClick={() => navigate(`/ops/projects/${p.id}`)} className="w-full text-left text-xs text-gray-300 hover:text-[#D4AF37] py-0.5 flex justify-between">
                <span className="truncate">{p.name}</span><span className="text-gray-500">{money(p.contract_value_cents)}</span>
              </button>
            ))}
          </div>
        )}
        {d.quotes?.length > 0 && (
          <div className="mt-2 border-t border-gray-800 pt-2">
            <span className="text-[10px] text-[#D4AF37] font-brand font-bold uppercase tracking-wider">Quotes</span>
            {d.quotes.slice(0, 5).map((q) => (
              <button key={q.id} onClick={() => navigate(`/ops/quotes/${q.id}`)} className="w-full text-left text-xs text-gray-300 hover:text-[#D4AF37] py-0.5 flex justify-between">
                <span className="truncate">{q.quote_number} · {q.title}</span><span className={q.status === 'accepted' ? 'text-green-400' : 'text-gray-500'}>{q.status}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const PbItemCard = ({ d }) => (
  <div className={cardCls} data-testid="foreman-pbitem-card">
    <Head icon={BookOpen} label="Price Book Item" />
    <div className="px-4 py-2.5 pb-3.5">
      <div className="text-white font-semibold text-sm">{d.name}</div>
      <div className="mt-1.5">
        <Row k="Category" v={d.category || 'Uncategorized'} />
        <Row k="Unit" v={d.unit_type} />
        <Row k="Default Price" v={d.standard_unit_price_cents != null ? money(d.standard_unit_price_cents) : 'Not set yet'} gold />
        {d.description && <Row k="Description" v={d.description} />}
      </div>
    </div>
  </div>
);

export const PriceBookAuditCard = ({ d, onAction, busy }) => {
  const [resolved, setResolved] = React.useState({});
  return (
    <div className="rounded-xl bg-[#161616] border border-gray-800/80 overflow-hidden w-full max-w-md" data-testid="foreman-audit-card">
      <Head icon={BookOpen} label="Price Book Audit" />
      <div className="px-4 py-2.5 space-y-3 pb-3.5">
        <p className="text-xs text-gray-400">{d.item_count} items · {d.duplicates?.length || 0} possible duplicates · {d.issues?.length || 0} with issues · {d.missing?.length || 0} common items missing</p>
        {d.duplicates?.length > 0 && (
          <div>
            <span className="text-[10px] text-amber-400 font-brand font-bold uppercase tracking-wider">Possible Duplicates</span>
            {d.duplicates.map((p, i) => resolved[`d${i}`] ? (
              <p key={i} className="text-[11px] text-gray-500 py-1">{resolved[`d${i}`]}</p>
            ) : (
              <div key={i} className="mt-1.5 rounded-lg bg-black/40 border border-gray-800 p-2.5">
                <div className="text-xs text-gray-200">{p.a.name} <span className="text-gray-500">({p.a.price_cents != null ? money(p.a.price_cents) : 'no price'})</span></div>
                <div className="text-xs text-gray-200">{p.b.name} <span className="text-gray-500">({p.b.price_cents != null ? money(p.b.price_cents) : 'no price'})</span></div>
                <div className="flex gap-2 mt-2">
                  <Btn primary disabled={busy} onClick={async () => {
                    const keep = p.a.price_cents != null || p.b.price_cents == null ? p.a : p.b;
                    const rem = keep === p.a ? p.b : p.a;
                    const r = await onAction({ type: 'merge_price_book_items', keep_item_id: keep.id, remove_item_id: rem.id });
                    if (r) setResolved((x) => ({ ...x, [`d${i}`]: r }));
                  }}>Merge</Btn>
                  <Btn disabled={busy} onClick={() => setResolved((x) => ({ ...x, [`d${i}`]: 'Kept separate' }))}>Keep Separate</Btn>
                </div>
              </div>
            ))}
          </div>
        )}
        {d.missing?.length > 0 && (
          <div>
            <span className="text-[10px] text-[#D4AF37] font-brand font-bold uppercase tracking-wider">Missing Common Items</span>
            <div className="mt-1.5 space-y-1 max-h-56 overflow-y-auto pr-1">
              {d.missing.map((m, i) => resolved[`m${i}`] ? (
                <p key={i} className="text-[11px] text-green-500/80">✓ {m.name} added</p>
              ) : (
                <div key={i} className="flex items-center justify-between gap-2 text-xs">
                  <span className="text-gray-300 truncate">{m.name} <span className="text-gray-600">· {m.category}</span></span>
                  <button disabled={busy} onClick={async () => {
                    const r = await onAction({ type: 'create_price_book_item', fields: { name: m.name, category: m.category, unit_type: m.unit_type } });
                    if (r) setResolved((x) => ({ ...x, [`m${i}`]: true }));
                  }} className="text-[#D4AF37] hover:text-[#F0CB5C] shrink-0 flex items-center gap-0.5 font-semibold" data-testid={`audit-add-${i}`}><Plus size={12} /> Add</button>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-600 mt-1.5">Added items start with no price — set your pricing in the Price Book (Foreman never invents prices).</p>
          </div>
        )}
        {d.issues?.length > 0 && (
          <div>
            <span className="text-[10px] text-gray-400 font-brand font-bold uppercase tracking-wider">Items Needing Attention</span>
            {d.issues.map((it) => <p key={it.id} className="text-[11px] text-gray-500">{it.name} — {it.problems.join(', ')}</p>)}
          </div>
        )}
      </div>
    </div>
  );
};

export const ActionCard = ({ card, onAction, busy }) => {
  if (!card) return null;
  if (card.type === 'quote') return <QuoteCard d={card.data} />;
  if (card.type === 'expense') return <ExpenseCard d={card.data} />;
  if (card.type === 'event') return <EventCard d={card.data} />;
  if (card.type === 'project') return <ProjectCard d={card.data} />;
  if (card.type === 'financial_summary') return <FinancialCard d={card.data} />;
  if (card.type === 'labor_summary') return <LaborCard d={card.data} />;
  if (card.type === 'material_summary') return <MaterialCard d={card.data} />;
  if (card.type === 'customer') return <CustomerCard d={card.data} />;
  if (card.type === 'pb_item') return <PbItemCard d={card.data} />;
  if (card.type === 'pricebook_audit') return <PriceBookAuditCard d={card.data} onAction={onAction} busy={busy} />;
  return null;
};

export const ResultChip = ({ r }) => (
  <div className={`inline-flex items-center gap-1.5 text-[11px] rounded-full px-2.5 py-1 border ${r.ok ? 'text-green-400 border-green-900/70 bg-green-950/30' : 'text-red-400 border-red-900/70 bg-red-950/30'}`} data-testid="ai-action-chip">
    {r.ok ? <CheckCircle2 size={11} /> : <XCircle size={11} />} {r.detail}
  </div>
);

const CONFIRM_META = {
  create_project: { label: 'Create Project', fields: (a) => [['Quote', a.quote_id?.slice(0, 8)], ['Name', a.fields?.name], ['Status', a.fields?.status || 'planning']] },
  create_expense: { label: 'New Expense', fields: (a) => [['Vendor', a.fields?.vendor_name], ['Description', a.fields?.description], ['Category', a.fields?.work_category || a.fields?.expense_type], ['Amount', a.fields?.total_cents != null ? money(a.fields.total_cents) : null], ['Date', a.fields?.expense_date || 'today']] },
  create_calendar_event: { label: 'New Calendar Event', fields: (a) => [['Title', a.fields?.title], ['Date', a.fields?.start_date], ['Time', a.fields?.start_time], ['Location', a.fields?.location]] },
  merge_price_book_items: { label: 'Merge Price Book Items', fields: (a) => [['Keep', a.keep_item_id?.slice(0, 8)], ['Archive', a.remove_item_id?.slice(0, 8)]] },
  create_price_book_item: { label: 'New Price Book Item', fields: (a) => [['Name', a.fields?.name], ['Category', a.fields?.category], ['Unit', a.fields?.unit_type], ['Price', a.fields?.standard_unit_price_cents != null ? money(a.fields.standard_unit_price_cents) : 'Not set']] },
};

export const ConfirmCard = ({ action, onConfirm, onCancel, busy }) => {
  const meta = CONFIRM_META[action.type] || { label: action.type, fields: () => [] };
  return (
    <div className="rounded-xl bg-[#161616] border border-[#D4AF37]/40 max-w-sm w-full" data-testid="foreman-confirm-card">
      <div className="px-4 pt-3">
        <span className="font-brand text-[10px] font-bold uppercase tracking-[0.18em] text-[#D4AF37]">Confirm · {meta.label}</span>
      </div>
      <div className="px-4 py-2.5">
        {meta.fields(action).filter(([, v]) => v).map(([k, v]) => <Row key={k} k={k} v={v} />)}
      </div>
      <div className="flex gap-2 px-4 pb-3">
        <Btn primary onClick={onConfirm} disabled={busy} data-testid="foreman-confirm-btn"><CheckCircle2 size={12} /> Confirm</Btn>
        <Btn onClick={onCancel} disabled={busy}>Cancel</Btn>
      </div>
    </div>
  );
};
